// Custom AppSync subscription transport — pure WebSocket against
// `appsync-realtime-api.*.amazonaws.com`, no MQTT.
//
// Replaces aws-appsync-subscription-link@3.1.x, which still negotiates
// MQTT during the handshake and consequently fails against AppSync APIs
// where MQTT support has been deprecated (server returns
// "[GraphQL error]: Subscriptions over MQTT is not supported." + 400
// on the HTTPS POST handshake, plus a runtime TypeError reading
// `subscriptionFailedCallback`). The v4.x line of that package fixes
// MQTT but requires @apollo/client@4.x as a peer; this app pins
// @apollo/client@^3.8.0, so an in-tree shim is the cleanest path.
//
// Protocol reference: AWS AppSync "Building a real-time WebSocket client"
//   docs.aws.amazon.com/appsync/latest/devguide/real-time-websocket-client.html
//
// Auth mode: AWS_LAMBDA. The Lambda authorizer expects the token in the
// `Authorization` field of the AppSync header object (no `Bearer` prefix is
// added here — the token provider returns the exact string the resolver
// expects, matching the HTTP-side jwtAuthLink shape in client.js).

import { ApolloLink, Observable } from '@apollo/client';
import { print } from 'graphql';
import { v4 as uuidv4 } from 'uuid';

const KA_FALLBACK_MS = 5 * 60 * 1000; // AppSync default connectionTimeoutMs
const KA_GRACE_MS = 5_000; // small slack on top of the server-advertised timeout
const RECONNECT_INITIAL_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
const CLOSE_DELAY_MS = 30_000; // keep WS warm this long after last unsubscribe

function deriveRealtime(graphqlEndpoint) {
  const u = new URL(graphqlEndpoint);
  const host = u.hostname;
  const realtimeHost = host.replace('appsync-api.', 'appsync-realtime-api.');
  return { host, realtimeUrl: `wss://${realtimeHost}${u.pathname}` };
}

function b64(value) {
  // browser-safe base64 of a UTF-8 string
  return btoa(unescape(encodeURIComponent(value)));
}

function buildConnectUrl(realtimeUrl, host, authToken) {
  const header = b64(JSON.stringify({ Authorization: authToken, host }));
  const payload = b64('{}');
  // Standard base64 contains `+`, `/`, and `=` which corrupt as query-string
  // values (`+` decodes to space, `=` may prematurely terminate parsing).
  // Wrap with encodeURIComponent so the AppSync WebSocket handshake survives
  // any auth-token contents. KAP-3xx 2026-05-14.
  return `${realtimeUrl}?header=${encodeURIComponent(header)}&payload=${encodeURIComponent(payload)}`;
}

export class AppSyncWsLink extends ApolloLink {
  constructor({ graphqlEndpoint, tokenProvider }) {
    super();
    const { host, realtimeUrl } = deriveRealtime(graphqlEndpoint);
    this.host = host;
    this.realtimeUrl = realtimeUrl;
    this.tokenProvider = tokenProvider;
    this.ws = null;
    this.state = 'closed'; // 'closed' | 'connecting' | 'open'
    this.connectPromise = null;
    this.subs = new Map(); // id -> { observer, startPayload }
    this.kaTimer = null;
    this.kaTimeoutMs = KA_FALLBACK_MS;
    this.idleCloseTimer = null;
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
  }

  request(operation) {
    return new Observable((observer) => {
      const id = uuidv4();
      const token = this.tokenProvider();
      if (!token) {
        observer.error(new Error('[appsync-ws] no auth token; cannot subscribe'));
        return;
      }
      const startPayload = {
        data: JSON.stringify({
          query: print(operation.query),
          variables: operation.variables || {},
        }),
        extensions: {
          authorization: { Authorization: token, host: this.host },
        },
      };
      // `started` tracks whether we've already sent a `start` frame for this
      // sub on the current connection. Prevents the duplicate-start race
      // where (a) request() fires start after _ensureConnected resolves AND
      // (b) the connection_ack handler also loops over queued subs and fires
      // start — AppSync rejects the second start with
      // "Duplicated operation with id <uuid>".
      this.subs.set(id, { observer, startPayload, started: false });

      if (this.idleCloseTimer) {
        clearTimeout(this.idleCloseTimer);
        this.idleCloseTimer = null;
      }

      if (this.state === 'open') {
        // Connection already established — send start now and mark.
        this._send({ id, type: 'start', payload: startPayload });
        const sub = this.subs.get(id);
        if (sub) sub.started = true;
      } else {
        // State is 'connecting' or 'closed'. _ensureConnected triggers the
        // connect; the connection_ack handler will send start for any subs
        // in the map whose `started` is still false. Do NOT send start here.
        this._ensureConnected().catch((err) => {
          if (this.subs.has(id)) {
            this.subs.delete(id);
            observer.error(err);
          }
        });
      }

      return () => {
        if (this.subs.has(id)) {
          this.subs.delete(id);
          if (this.state === 'open') {
            this._send({ id, type: 'stop' });
          }
        }
        if (this.subs.size === 0 && this.state === 'open') {
          // Keep WS warm briefly in case a component re-subscribes on
          // remount; avoids needless connect/disconnect churn.
          this.idleCloseTimer = setTimeout(() => this._closeWs('idle'), CLOSE_DELAY_MS);
        }
      };
    });
  }

  _send(obj) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(obj));
    }
  }

  _ensureConnected() {
    if (this.state === 'open') return Promise.resolve();
    if (this.connectPromise) return this.connectPromise;
    this.connectPromise = new Promise((resolve, reject) => {
      const token = this.tokenProvider();
      if (!token) {
        this.connectPromise = null;
        reject(new Error('[appsync-ws] no auth token'));
        return;
      }
      this.state = 'connecting';
      const url = buildConnectUrl(this.realtimeUrl, this.host, token);
      let acked = false;

      try {
        this.ws = new WebSocket(url, ['graphql-ws']);
      } catch (err) {
        this.state = 'closed';
        this.connectPromise = null;
        reject(err);
        return;
      }

      this.ws.addEventListener('open', () => {
        this._send({ type: 'connection_init' });
      });

      this.ws.addEventListener('message', (event) => {
        let msg;
        try {
          msg = JSON.parse(event.data);
        } catch (_e) {
          return;
        }
        if (msg.type === 'connection_ack' && !acked) {
          acked = true;
          this.state = 'open';
          this.kaTimeoutMs = msg.payload?.connectionTimeoutMs || KA_FALLBACK_MS;
          this._resetKaTimer();
          this.reconnectAttempts = 0;
          this.connectPromise = null;
          // Send start for any sub registered before/during the connect that
          // hasn't been started yet. The `started` flag prevents a duplicate
          // start when request() already fired one on a still-open connection.
          for (const [id, sub] of this.subs) {
            if (!sub.started) {
              this._send({ id, type: 'start', payload: sub.startPayload });
              sub.started = true;
            }
          }
          resolve();
          return;
        }
        this._handleMessage(msg);
      });

      this.ws.addEventListener('error', () => {
        if (!acked) {
          this.connectPromise = null;
          reject(new Error('[appsync-ws] connection error before ack'));
        }
        // close handler will trigger reconnect if subs remain.
      });

      this.ws.addEventListener('close', (ev) => {
        this._onSocketClose(ev);
      });
    });
    return this.connectPromise;
  }

  _handleMessage(msg) {
    switch (msg.type) {
      case 'ka':
        this._resetKaTimer();
        return;
      case 'start_ack':
        return; // subscription registered
      case 'data': {
        const sub = this.subs.get(msg.id);
        // AppSync `data` payload already matches Apollo's FetchResult shape:
        // { data, errors? } — pass it straight through.
        if (sub) sub.observer.next(msg.payload);
        return;
      }
      case 'complete': {
        const sub = this.subs.get(msg.id);
        if (sub) sub.observer.complete();
        this.subs.delete(msg.id);
        return;
      }
      case 'error': {
        const sub = msg.id ? this.subs.get(msg.id) : null;
        const err = new Error(
          msg.payload?.errors?.[0]?.message || '[appsync-ws] subscription error',
        );
        err.payload = msg.payload;
        if (sub) {
          sub.observer.error(err);
          this.subs.delete(msg.id);
        } else {
          // Connection-level error with no sub id — surface for debugging.
          // eslint-disable-next-line no-console
          console.warn('[appsync-ws] server error', msg.payload);
        }
        return;
      }
      default:
        return;
    }
  }

  _resetKaTimer() {
    if (this.kaTimer) clearTimeout(this.kaTimer);
    this.kaTimer = setTimeout(() => {
      // AppSync stopped sending keep-alives — connection is dead.
      // eslint-disable-next-line no-console
      console.warn('[appsync-ws] keep-alive timeout; closing socket');
      this._closeWs('ka_timeout');
    }, this.kaTimeoutMs + KA_GRACE_MS);
  }

  _closeWs(reason) {
    if (this.kaTimer) {
      clearTimeout(this.kaTimer);
      this.kaTimer = null;
    }
    if (this.idleCloseTimer) {
      clearTimeout(this.idleCloseTimer);
      this.idleCloseTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close(1000, reason || 'client_close');
      } catch (_e) {
        // ignore
      }
      this.ws = null;
    }
    this.state = 'closed';
  }

  _onSocketClose() {
    if (this.kaTimer) {
      clearTimeout(this.kaTimer);
      this.kaTimer = null;
    }
    this.state = 'closed';
    this.ws = null;
    this.connectPromise = null;
    // Mark all subs as un-started so reconnect re-registers them.
    for (const sub of this.subs.values()) {
      sub.started = false;
    }
    // Reconnect with backoff if there are still active subs.
    if (this.subs.size > 0) {
      this._scheduleReconnect();
    }
  }

  _scheduleReconnect() {
    if (this.reconnectTimer) return;
    const backoff = Math.min(
      RECONNECT_INITIAL_MS * Math.pow(2, this.reconnectAttempts),
      RECONNECT_MAX_MS,
    );
    const jitter = Math.random() * backoff * 0.3;
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.subs.size === 0) return;
      this._ensureConnected().catch(() => {
        // _onSocketClose will re-schedule.
      });
    }, backoff + jitter);
  }
}
